import React from "react";
import Button from "../components/Button";
import ScrollArrow from "../components/ScrollArrow.tsx";
import useInView from "../hooks/useInView";

export default function ReneePage() {
  const [heroRef, heroInView] = useInView();
  const [storyRef, storyInView] = useInView({ rootMargin: "200px 0px 0px 0px" });
  const [cvRef, cvInView] = useInView();
  const [scrollY, setScrollY] = React.useState(0);
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  React.useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const fadeIn = (inView, delay = 0) => ({
    opacity: inView ? 1 : 0,
    filter: `blur(${inView ? 0 : 10}px)`,
    transform: `translateY(${inView ? 0 : -20}px)`,
    transition: `opacity 0.8s ease-out ${delay}s, filter 0.8s ease-out ${delay}s, transform 0.8s ease-out ${delay}s`,
  });

  return (
    <div className="bg-black text-white overflow-hidden">

      {/* HERO */}
      <section
        ref={heroRef}
        className="relative flex"
        style={{ height: isMobile ? "60vh" : "100vh", marginTop: isMobile ? "60px" : undefined }}
      >
        {/* LEFT: RENÉ */}
        <div className="relative w-1/2 h-full">
          <img
            src="/images/rene.webp"
            alt="Renée Gebhardt, Freelance Art Director and Filmmaker, Berlin"
            className="absolute inset-0 w-full h-full object-cover"
          />
          {isMobile && (
            <div style={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              height: "100px",
              background: "linear-gradient(to top, rgba(255,255,255,0.08), transparent)",
              pointerEvents: "none",
            }} />
          )}
          <div
            className="absolute left-0 right-0 flex flex-col items-center leading-none"
            style={{ ...fadeIn(heroInView, 0.8), bottom: isMobile ? "30px" : "80px" }}
          >
            <h1
              className="font-black tracking-tight text-white"
              style={{
                fontFamily: "Archivo, sans-serif",
                fontVariationSettings: '"wght" 600, "wdth" 75',
                lineHeight: "0.85",
                fontSize: isMobile ? "10vw" : "7vw",
              }}
            >
              RENÉ
            </h1>
            <p
              className="text-white font-light tracking-wider mt-1"
              style={{ fontFamily: "Inter, sans-serif", fontSize: isMobile ? "14px" : "24px" }}
            >
              2006
            </p>
          </div>
        </div>

        {/* RIGHT: RENÉE */}
        <div className="relative w-1/2 h-full">
          <img
            src="/images/renee.webp"
            alt="Renée Gebhardt, Art Director and Co-Founder, Berlin"
            className="absolute inset-0 w-full h-full object-cover"
          />
          {isMobile && (
            <div style={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              height: "100px",
              background: "linear-gradient(to top, rgba(255,255,255,0.08), transparent)",
              pointerEvents: "none",
            }} />
          )}
          <div
            className="absolute left-0 right-0 flex flex-col items-center leading-none"
            style={{ ...fadeIn(heroInView, 1), bottom: isMobile ? "30px" : "80px" }}
          >
            <h1
              className="font-black tracking-tight text-white"
              style={{
                fontFamily: "Archivo, sans-serif",
                fontVariationSettings: '"wght" 600, "wdth" 75',
                lineHeight: "0.85",
                fontSize: isMobile ? "10vw" : "7vw",
              }}
            >
              RENÉE
            </h1>
            <p
              className="text-white font-light tracking-wider mt-1"
              style={{ fontFamily: "Inter, sans-serif", fontSize: isMobile ? "14px" : "24px" }}
            >
              2026
            </p>
          </div>
        </div>

        {/* Scroll Arrow - desktop only */}
        {!isMobile && (
          <div
            className="absolute bottom-32 left-1/2 -translate-x-1/2"
            style={{ opacity: heroInView ? Math.max(0, 1 - scrollY / 200) : 0 }}
          >
            <ScrollArrow />
          </div>
        )}
      </section>

<div style={{ textAlign: "center", padding: "12px 0" }}>
  <p className="text-zinc-600" style={{ fontSize: "13px", fontFamily: "Inter, sans-serif" }}>
    ©Johannes Stoll · ©Renée Gebhardt
  </p>
</div>

      {/* THE STORY */}
      <section
        ref={storyRef}
        className="section-padding bg-black"
        style={{
          paddingLeft: isMobile ? "15px" : undefined,
          paddingRight: isMobile ? "15px" : undefined,
        }}
      >
        <div className="container-max">

          {/* DESKTOP: two-column */}
          {!isMobile && (
            <div className="grid grid-cols-2 gap-20 items-start">
              {/* LEFT: Headline + Photos */}
              <div className="relative">
                <div className="text-right mb-24">
                  <h2 className="heading-section uppercase" style={fadeIn(storyInView, 0.3)}>
                    THE<br />STORY
                  </h2>
                </div>
                <div className="relative" style={fadeIn(storyInView, 0.4)}>
                  <div className="relative w-4/5" style={{ transform: "rotate(1.5deg)", opacity: 0.85, zIndex: 1 }}>
                    <div style={{ aspectRatio: "3/2" }}>
                      <img src="/images/artist-pervert-bts.webp" alt="Behind the scenes of The Artist and the Pervert documentary, directed by Renée Gebhardt and Beatrice Behn" className="w-full h-full object-cover" style={{ borderRadius: "2px" }} />
                    </div>
                  </div>
                  <div className="relative w-4/5 ml-auto" style={{ transform: "rotate(-1.5deg)", opacity: 0.85, marginRight: "40px", zIndex: 2 }}>
                    <div style={{ aspectRatio: "4/3" }}>
                      <img src="/images/kh-event.webp" alt="Karada House Berlin event: queer kink and art space in a former church in Moabit" className="w-full h-full object-cover" style={{ borderRadius: "2px" }} />
                    </div>
                  </div>
                </div>
              </div>
              {/* RIGHT: Text */}
              <div className="body-large" style={fadeIn(storyInView, 0.5)}>
                <p className="mb-6">I started in advertising.<br />Big agencies. Big brands. Big expectations.<br />I learned how ideas survive clients, budgets, and pressure.<br />That education still pays my bills.</p>
                <p className="mb-6"><strong className="text-white">It also showed me exactly where creativity dies.</strong></p>
                <p className="mb-6">In 2016, Beatrice Behn and I read about Georg Friedrich Haas and Mollena Williams-Haas in the New York Times.<br />A famous composer who married a BDSM educator.<br />People lost their shit.</p>
                <p className="mb-6">We asked if we could film them. They said yes.<br />So we stalked them with a camera for a year.</p>
                <p className="mb-6">We called it <i>’The Artist & The Pervert.‘</i><br />A documentary about living without compromise.</p>
                <p className="mb-6"><strong className="text-white">I wanted that too.</strong></p>
                <p className="mb-6">So I stopped holding myself back.<br />I co-created Karada House, because I was tired of avoiding uncomfortable questions.<br />Especially my own.</p>
                <p className="mb-6"><strong className="text-white">I still do commercial campaigns.</strong></p>
                <p className="mb-6">The approach hasn’t changed:<br />understand what people actually feel,<br />show them something they didn’t expect,<br />and don’t make it boring.</p>
                <p className="mb-6"><strong className="text-white">I don’t make work that's easy to ignore.</strong></p>
              </div>
            </div>
          )}

          {/* MOBILE: single column */}
          {isMobile && (
            <div style={{ opacity: 1 }}>
              <h2
                className="heading-section uppercase"
                style={{ textAlign: "center", marginBottom: "32px" }}
              >
                THE STORY
              </h2>
              <div className="body-large">
                <p className="mb-6">I started in advertising.<br />Big agencies. Big brands. Big expectations.<br />I learned how ideas survive clients, budgets, and pressure.<br />That education still pays my bills.</p>
                <p className="mb-6"><strong className="text-white">It also showed me exactly where creativity dies.</strong></p>
                <p className="mb-6">In 2016, Beatrice Behn and I read about Georg Friedrich Haas and Mollena Williams-Haas in the New York Times.<br />A famous composer who married a BDSM educator. – People lost their shit.</p>
                <p className="mb-6">We asked if we could film them. They said yes. So we stalked them with a camera for a year.</p>
                <p className="mb-6">We called it <i>’The Artist & The Pervert.‘</i><br />A documentary about living without compromise.</p>

                {/* Photo 1 – before "I wanted that too" */}
                <div style={{ marginBottom: "50px", marginTop: "50px", opacity: 0.85 }}>
                  <div style={{ aspectRatio: "3/2" }}>
                    <img src="/images/artist-pervert-bts.webp" alt="Behind the scenes of The Artist and the Pervert documentary, directed by Renée Gebhardt and Beatrice Behn" className="w-full h-full object-cover" style={{ borderRadius: "2px" }} />
                  </div>
                </div>

                <p className="mb-6"><strong className="text-white">I wanted that too.</strong></p>
                <p className="mb-6">So I stopped holding myself back.<br />I co-created Karada House, because I was tired of avoiding uncomfortable questions.<br />Especially my own.</p>
                <p className="mb-6"><strong className="text-white">I still do commercial campaigns.</strong></p>
                <p className="mb-6">The approach hasn’t changed:<br />understand what people actually feel,<br />show them something they didn’t expect,<br />and don’t make it boring.</p>
                <p className="mb-6"><strong className="text-white">I don’t make work that’s easy to ignore.</strong></p>

                {/* Photo 2 – after story */}
                <div style={{ marginTop: "50px", opacity: 0.85 }}>
                  <div style={{ aspectRatio: "4/3" }}>
                    <img src="/images/kh-event.webp" alt="Karada House Berlin event: queer kink and art space in a former church in Moabit" className="w-full h-full object-cover" style={{ borderRadius: "2px" }} />
                  </div>
                </div>
              </div>
            </div>
          )}

        </div>
      </section>

      {/* THE RECEIPTS */}
      <section
        ref={cvRef}
        className="section-padding bg-black"
        style={{
          transform: isMobile ? "none" : "rotate(0deg)",
          transformOrigin: "center center",
          paddingLeft: isMobile ? "15px" : undefined,
          paddingRight: isMobile ? "15px" : undefined,
          paddingTop: isMobile ? "40px" : undefined,
        }}
      >
        <div className="max-w-[900px] mx-auto">
          <h2
            className="heading-section uppercase mb-12"
            style={{
              ...fadeIn(cvInView, 0.3),
              marginLeft: isMobile ? "0" : "-75px",
              textAlign: isMobile ? "center" : undefined,
            }}
          >
            THE<br />RECEIPTS
          </h2>

          <div className="text-sm leading-relaxed text-zinc-400" style={fadeIn(cvInView, 0.5)}>
            <p className="body-large mb-8 text-zinc-300">
              If you need the full timeline,<br />here are the facts.
            </p>

            {/* NOW */}
            <div className="mb-12">
              <h3 className="heading-work text-white mb-4 uppercase tracking-wider">NOW</h3>
              <div className="mb-6">
                <p className="text-white mb-1"><strong>Freelance Art-Director</strong> (2014–present)</p>
                <p className="text-zinc-500">Working with major agencies and clients Germany and International. Clients include antoni, BBDO, Edelman, Erdmannpeisker, Fischer Appelt, HEIMAT, Jung von Matt, Ogilvy Germany, Ressourcenmangel, Scholz & Friends, THE GOODWINS, Serviceplan, THJNK Berlin, Zum Goldenen Hirschen.</p>
              </div>
              <div>
                <p className="text-white mb-1"><strong>Karada House Co-Founder & Director</strong> (2019–present)</p>
                <p className="text-zinc-500">queer collaborative art space in Berlin that explores the intersection of body, art, and politics. Hosting conferences, workshops, exhibitions, and performances for people on the LGBTQIA+ spectrum and other marginalized communities.</p>
              </div>
            </div>

            {/* BEFORE */}
            <div className="mb-12">
              <h3 className="heading-work text-white mb-4 uppercase tracking-wider">BEFORE</h3>
              <div className="mb-4">
                <p className="text-white mb-1"><strong>Senior Art-Director, Scholz & Friends</strong> (2010–2014)</p>
                <p className="text-zinc-500">Led creative campaigns for clients including Aktion Aufschrei, Bayer, Caparol, Die PARTEI, Das Handwerk, Fisherman’s Friend, Penny, Saturn, and Stihl.</p>
              </div>
              <div className="mb-4">
                <p className="text-white mb-1"><strong>Junior Art-Director, DDB Berlin</strong> (2007–2010)</p>
                <p className="text-zinc-500">Worked on large-scale campaigns for Volkswagen, IKEA Österreich, Bild.de, Bosch Hausgeräte, Freistaat Thüringen.</p>
              </div>
              <div className="mb-4">
                <p className="text-white mb-1"><strong>Book-Author, Verlag Hermann Schmidt Mainz</strong> (2005)</p>
                <p className="text-zinc-500">’Don’t Panic! Der Praktikantenguide‘, 320-page guide for interns in the creative industry.</p>
              </div>
              <div>
                <p className="text-white mb-1"><strong>Intern, Jung von Matt</strong> (2005)</p>
                <p className="text-zinc-500">BMW, MINI, Nintendo, TV Spielfilm and BILD.</p>
              </div>
            </div>

            {/* THINGS THAT LASTED */}
            <div className="mb-12 border border-zinc-700 p-6" style={{ borderRadius: "6px" }}>
              <h3 className="heading-work text-white mb-6 uppercase tracking-wider">THINGS THAT LASTED</h3>
              <div className="mb-6">
                <p className="text-white mb-1"><strong>Karada House</strong> (2019)</p>
                <p className="text-zinc-500 mb-2">Turned a church into a queer art space in Berlin.<br />1600+ events in the first 6 years.</p>
                <p className="text-zinc-600 italic">Deutschlandfunk Nova, Siegessäule, TAZ, Tagesspiegel wrote about it.<br />Still running.</p>
              </div>
              <div className="mb-6">
                <p className="text-white mb-1"><strong>The Artist & The Pervert</strong> (2017)</p>
                <p className="text-zinc-500 mb-2">Documentary about composer Georg Friedrich Haas and performance artist Mollena Williams-Haas.<br />40+ film festivals: HotDocs Canada, Thessaloniki Doc Fest, Raindance.<br />Best Documentary (Pornfilmfestival Berlin, CineKink NYC).</p>
                <p className="text-zinc-600 italic">FAZ Feuilleton, Deutschlandfunk, Siegessäule.<br />Cited in academic research papers, <a href="https://en.wikipedia.org/wiki/The_Artist_%26_The_Pervert" target="_blank" rel="noopener noreferrer" style={{color:'inherit'}}>Wikipedia</a>.</p>              </div>
              <div className="mb-6">
                <p className="text-white mb-1"><strong>PROJEKTIL</strong> (2006)</p>
                <p className="text-zinc-500 mb-2">Establishing international lecture series at Bauhaus Universität Weimar.<br />Stefan Sagmeister, Kurt Weidemann, Ruedi Baur, Amir Kassaei spoke there.</p>
                <p className="text-zinc-600 italic">Co-Founded with Björn Kernspeckt, Sebastian Locke<br />20 years later, still running.</p>
              </div>
              <div>
                <p className="text-white mb-1"><strong>DON’T PANIC! Der Praktikantenguide</strong> (2005)</p>
                <p className="text-zinc-500 mb-2">320-page guide for creative interns.<br />Verlag Hermann Schmidt Mainz.</p>
                <p className="text-zinc-600 italic">Co-authored with Björn Kernspeckt, Sebastian Locke, Antje Gerwien.<br />Presented at Frankfurter Buchmesse</p>
              </div>
            </div>

            {/* EDUCATION & AWARDS */}
            <div>
              <h3 className="heading-work text-white mb-4 uppercase tracking-wider">EDUCATION & AWARDS</h3>
              <div className="mb-4">
                <p className="text-white mb-1"><strong>Bauhaus Universität Weimar</strong> (2002–2007)</p>
                <p className="text-zinc-500 mb-2">Visual Communication. Founded the international Bauhaus lecture series PROJEKTIL (still running today).</p>
              </div>
              <div className="mb-4">
                <p className="text-white mb-1"><strong>Cannes Young Lions Competition</strong> (2004)</p>
                <p className="text-zinc-500 mb-2">Together with Björn Kernspeckt, represented Germany at the international Young Lions Competition in Cannes.</p>
              </div>
              <div>
                <p className="text-white mb-2"><strong>200+ awards including:</strong></p>
                <p className="text-zinc-500 mb-1">10× Cannes Lions<br />Red Dot Grand Prix<br />ADC Gold<br />One Show Gold<br />LEAD Award Campaign of the Year</p>
                <p className="text-zinc-500 mt-3">Film festival wins: Best Documentary (Pornfilmfestival Berlin), Audience Choice (CineKink NYC), 40+ screenings worldwide</p>
                <p className="text-zinc-600 mt-3 italic">Also: "Flop of the Week" in W&V.</p>
              </div>
            </div>
          </div>

          {/* CTAs */}
          <div
            style={{
              ...fadeIn(cvInView, 0.7),
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              gap: "16px",
              marginTop: "48px",
              alignItems: isMobile ? "center" : undefined,
            }}
          >
<a href="/images/ReneeGebhardt-CV.pdf" download style={{ textDecoration: 'none' }}>
  <Button>DOWNLOAD CV</Button>
</a>            <Button href="/work">SEE THE WORK</Button>
          </div>
        </div>
      </section>
    </div>
  );
}